import * as classnames from 'classnames';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { TeamMessageStatistics } from '@mm/state/helpers/billing-subscription-tab-helpers';
import { ChannelIcon } from '../../../editor/features/liveChat/components/ChannelIcon';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { EnhancedOutgoingMessagesStatistic } from '@mm/state/model/ConnectedPageModel';
import { SubscriptionModel } from '@mm/state/model/SubscriptionModel';
import { selectBasicPages } from '@mm/state/selectors/facebookPageSelector';
import { StoreState } from '@mm/state/store-interfaces';
import { abbreviateNumber, getPercentage } from '@mm/utils';
import { usePhoneNumber } from '../../hooks/use-phone-number';
import { PrimaryToolTip } from '../PrimaryToolTip/PrimaryToolTip';
import './UsagePercentageBar.pcss';

type Props = {
  className: string;
  fbPage: EnhancedBillingPage;
  isTeam?: boolean;
  isCustomTeam?: boolean;
  anyPageHasSmsActivated?: boolean;
};

export const getUsedAndTotal = (
  statistics: TeamMessageStatistics | EnhancedOutgoingMessagesStatistic,
  subscription?: SubscriptionModel,
) => {
  let sentAllotment = 0;
  if (subscription && 'maxSendAllotment' in subscription) {
    sentAllotment = subscription.maxSendAllotment;
  }
  if ('utilizationPercentage' in statistics) {
    return [
      (statistics as EnhancedOutgoingMessagesStatistic).count,
      sentAllotment ||
        (statistics as EnhancedOutgoingMessagesStatistic).outgoingMessageLimit,
    ];
  }
  return [
    (statistics as TeamMessageStatistics).used,
    sentAllotment || (statistics as TeamMessageStatistics).messagesLimit,
  ];
};

// tslint:disable-next-line: cyclomatic-complexity
export const UsagePercentageBar = (props: Props) => {
  const { className, isTeam, isCustomTeam, anyPageHasSmsActivated, fbPage } =
    props;
  const { messagesStatistic: statistics, subscription } = fbPage;
  const [used, total] = getUsedAndTotal(statistics, subscription);
  const usedPercent = getPercentage(used, total);
  const overTotal = false;
  const creditsRatio = useSelector(
    (state: StoreState) => state.env.creditsQuantity,
  );
  const connectedPages = useSelector(selectBasicPages);
  const { isSmsSupported } = usePhoneNumber();
  const isInstagramActivated = fbPage?.instagramActivated;
  const anyPageHasInstagramActivated = useMemo(
    () => connectedPages.filter(p => p.instagramActivated).length > 0,
    [connectedPages],
  );

  const showNAForCustomTeam =
    isCustomTeam && fbPage.lockStrategy === 'twilio_level';

  const showInstagramCounter = isTeam
    ? anyPageHasInstagramActivated
    : isInstagramActivated;

  const classList = classnames('usage-percentage-bar', className, {
    'usage-percentage-bar--over-total': overTotal,
  });

  const percentStyle = {
    width: `${usedPercent}%`,
  };

  const showOveragesBar = false;

  let relativeUsedPercentage;
  if (overTotal) {
    relativeUsedPercentage = Math.floor(
      showOveragesBar
        ? Math.max((total * 100) / used, 5)
        : (total * 100) / used,
    );
  } else {
    relativeUsedPercentage = Math.floor((used * 100) / total);
  }

  const overagesBarStyle = {
    left: `${relativeUsedPercentage > 100 ? 100 : relativeUsedPercentage}%`,
  };

  const messagesLeft = total - used > 0 ? total - used : 0;

  const getFormatedCountLeft = (ratio: number) => {
    const messagesOfThisTypeLeft = Math.floor(messagesLeft / ratio);
    return ` ${abbreviateNumber(messagesOfThisTypeLeft)} `;
  };

  const sendCreditsUsed =
    (isTeam
      ? fbPage?.outgoingMessageUsage?.prevSubscriptionMessagingCount
      : fbPage?.prevOutgoingMessagesStatistic?.count) || 0;

  const showSendCreditsUsed = isTeam || fbPage?.prevOutgoingMessagesStatistic;

  return (
    <div className={classList}>
      <div className="usage-percentage-bar__body-wrapper">
        <div className="usage-percentage-bar__bar-title">Send Credits Used</div>
        <div className="usage-percentage-bar__bar">
          <div
            style={percentStyle}
            className="usage-percentage-bar__used-percentage"
          />
          {overTotal && showOveragesBar && (
            <div
              className="usage-percentage-bar__overages-bar"
              style={overagesBarStyle}
            />
          )}

          <div className="usage-percentage-bar__usage-overlay">
            {abbreviateNumber(used)} of {abbreviateNumber(total)}
          </div>
        </div>
        <div className="usage-percentage-bar__messages-left">
          Messages left:
          <ChannelIcon
            className="usage-percentage-bar__icon-svg"
            channel="facebook"
          />
          <div className="usage-percentage-bar__messages-type">
            {showNAForCustomTeam
              ? 'n/a'
              : getFormatedCountLeft(creditsRatio?.facebookPage || 1)}
          </div>
          {isSmsSupported && !fbPage.redemption?.instachamp && (
            <div className="usage-percentage-bar__sms-info">
              or
              <ChannelIcon
                className="usage-percentage-bar__icon-svg"
                channel="sms"
              />
              <div className="usage-percentage-bar__messages-type">SMS:</div>
              <PrimaryToolTip
                anchorElement={
                  <div className="usage-percentage-bar__popover-anchor">
                    {getFormatedCountLeft(creditsRatio?.sms || 10)}
                  </div>
                }
                useHover
                withoutLine
                classNameContainer="usage-percentage-bar__popover"
                classNameArrow="usage-percentage-bar__popover-arrow"
              >
                1 SMS message = {creditsRatio?.sms || 10} send credits
              </PrimaryToolTip>
              <div className="usage-percentage-bar__messages-type">
                &nbsp;/ MMS:
              </div>
              <PrimaryToolTip
                anchorElement={
                  <div className="usage-percentage-bar__popover-anchor">
                    {getFormatedCountLeft(creditsRatio?.mms || 20)}
                  </div>
                }
                useHover
                withoutLine
                classNameContainer="usage-percentage-bar__popover"
                classNameArrow="usage-percentage-bar__popover-arrow"
              >
                1 MMS message = {creditsRatio?.mms || 20} send credits
              </PrimaryToolTip>
            </div>
          )}
          or
          <div className="usage-percentage-bar__channel-stats">
            <ChannelIcon
              className="usage-percentage-bar__icon-svg"
              channel="webchat"
            />
            <div className="usage-percentage-bar__messages-type">
              {showNAForCustomTeam
                ? 'n/a'
                : getFormatedCountLeft(creditsRatio?.facebookPage || 1)}
            </div>
          </div>
          {showInstagramCounter && (
            <div className="usage-percentage-bar__instagram-info">
              or
              <div className="usage-percentage-bar__channel-stats">
                <ChannelIcon
                  className="usage-percentage-bar__icon-svg"
                  channel="instagram"
                />
                <div className="usage-percentage-bar__messages-type">
                  {showNAForCustomTeam
                    ? 'n/a'
                    : getFormatedCountLeft(creditsRatio?.instagram || 1)}
                </div>
              </div>
            </div>
          )}
        </div>
        {showSendCreditsUsed && (
          <div className="usage-percentage-bar__messages-left--label">
            Send credits used last{' '}
            {subscription?.billingPeriodLength === 'annually'
              ? 'year'
              : 'month'}
            :{' '}
            <span className="usage-percentage-bar__messages-type">
              {abbreviateNumber(sendCreditsUsed)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
